import React, { useLayoutEffect, useMemo, useRef } from 'react';

import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd/es/table/InternalTable';
import styled from 'styled-components';

import { SimpleListTableEmptyState } from 'components/lib/SimpleListModalComponents';
import { SimpleListTable } from 'components/lib/SimpleListTable';
import { useAppSelector } from 'store/hook';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';

import {
  ActionsCell,
  CompactActionButton,
  compactContactTableStyles,
  fillContactTableStyles,
  OwnerText,
  renderContactIdentity,
  renderMailboxStatus,
} from './ShopperContactsTableShared';
import { ShopperEmailType } from '../../types';

const BaseTable = (props: TableProps<ShopperEmailType>) => {
  return <Table<ShopperEmailType> {...props} />;
};

const StyledTable = styled(BaseTable)`
  ${SimpleListTable}
  ${compactContactTableStyles}
  ${fillContactTableStyles}

  &&&&&& {
    width: 100%;
  }
`;

const LoadMoreTrigger = styled.div`
  min-height: 1px;
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  padding-bottom: 8px;
`;

type Props = {
  shopperContacts: ShopperEmailType[];
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  linkingContactId: number | null;
  onLink: (shopperContact: ShopperEmailType) => void | Promise<void>;
};

const AvailableShopperContactsTable = ({
  shopperContacts,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  linkingContactId,
  onLink,
}: Props) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const showOwnerColumn = Boolean(currentUser?.isSuperuser);
  const columns = useMemo<ColumnsType<ShopperEmailType>>(
    () => [
      {
        title: 'Contact',
        dataIndex: 'name',
        key: 'contact',
        render: (_value: string | undefined, record) => renderContactIdentity(record),
      },
      ...(showOwnerColumn
        ? [
            {
              title: 'Owner',
              dataIndex: 'owner',
              key: 'owner',
              width: 220,
              render: (_value: ShopperEmailType['owner'], record: ShopperEmailType) => {
                const ownerName = record.owner?.full_name?.trim() || record.owner?.email || 'Unassigned';
                return <OwnerText>{ownerName}</OwnerText>;
              },
            },
          ]
        : []),
      {
        title: 'Mailbox Status',
        dataIndex: 'mailbox_connection_status',
        key: 'mailbox_connection_status',
        width: 160,
        render: (value: string | null | undefined, record) => renderMailboxStatus(value, record.email),
      },
      {
        title: '',
        key: 'actions',
        width: 100,
        render: (_value, record) => {
          const isLastContact = record.id === shopperContacts[shopperContacts.length - 1]?.id;
          const isLinking = linkingContactId === record.id;
          return (
            <ActionsCell ref={isLastContact && hasNextPage ? loadMoreTriggerRef : undefined}>
              <CompactActionButton
                loading={isLinking}
                disabled={linkingContactId !== null}
                onClick={() => onLink(record)}
              >
                Link
              </CompactActionButton>
            </ActionsCell>
          );
        },
      },
    ],
    [hasNextPage, linkingContactId, onLink, shopperContacts, showOwnerColumn]
  );

  useLayoutEffect(() => {
    const scrollContainer = tableContainerRef.current?.querySelector<HTMLElement>('.ant-table-body') ?? null;
    scrollContainerRef.current = scrollContainer;

    return () => {
      scrollContainerRef.current = null;
    };
  }, [isLoading, shopperContacts.length]);

  useInfiniteScroll({
    rootRef: scrollContainerRef,
    triggerRef: loadMoreTriggerRef,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });

  return (
    <Container ref={tableContainerRef}>
      <StyledTable
        rowKey="id"
        columns={columns}
        dataSource={shopperContacts}
        pagination={false}
        scroll={{ y: '100%' }}
        loading={isLoading}
        locale={{
          emptyText: (
            <SimpleListTableEmptyState>
              No available shopper contacts found.
              {hasNextPage ? <LoadMoreTrigger ref={loadMoreTriggerRef} /> : null}
            </SimpleListTableEmptyState>
          ),
        }}
      />
    </Container>
  );
};

export default AvailableShopperContactsTable;
